import { ModuleCategory } from '../../types/subscriptions';
import type { Module, OrganizationModule } from '../../types/subscriptions';

/**
 * Group modules by category
 * @param modules - List of modules
 * @returns Modules grouped by category
 */
export const groupModulesByCategory = (
  modules: Module[]
): Record<ModuleCategory, Module[]> => {
  const grouped = {} as Record<ModuleCategory, Module[]>;

  // Initialize all categories
  Object.values(ModuleCategory).forEach(category => {
    grouped[category as ModuleCategory] = [];
  });

  // Group modules
  modules.forEach(module => {
    grouped[module.category].push(module);
  });

  return grouped;
};

/**
 * Get category display label
 * @param category - Module category enum
 * @returns Human-readable label
 */
export const getCategoryLabel = (category: ModuleCategory): string => {
  const labels: Record<ModuleCategory, string> = {
    [ModuleCategory.CORE]: 'Core Features',
    [ModuleCategory.OPERATIONS]: 'Operations',
    [ModuleCategory.SALES]: 'Sales & POS',
    [ModuleCategory.LOGISTICS]: 'Logistics',
    [ModuleCategory.ANALYTICS]: 'Analytics & Reporting',
    [ModuleCategory.INTEGRATIONS]: 'Integrations',
  };
  return labels[category];
};

/**
 * Get category color for UI
 * @param category - Module category enum
 * @returns Color hex code
 */
export const getCategoryColor = (category: ModuleCategory): string => {
  const colors: Record<ModuleCategory, string> = {
    [ModuleCategory.CORE]: '#6366F1', // primary
    [ModuleCategory.OPERATIONS]: '#3B82F6', // blue
    [ModuleCategory.SALES]: '#10B981', // success/green
    [ModuleCategory.LOGISTICS]: '#F59E0B', // warning/amber
    [ModuleCategory.ANALYTICS]: '#8B5CF6', // accent/violet
    [ModuleCategory.INTEGRATIONS]: '#06B6D4', // cyan
  };
  return colors[category];
};

/**
 * Check if a module is currently in trial
 * @param orgModule - Organization module record
 * @returns True if trial and not expired
 */
export const isModuleTrial = (orgModule: OrganizationModule): boolean => {
  if (!orgModule.isTrial || !orgModule.trialEndsAt) return false;

  const trialEnd = new Date(orgModule.trialEndsAt);
  return trialEnd > new Date();
};

/**
 * Get days remaining in trial
 * @param trialEndsAt - Trial end date string
 * @returns Number of days remaining (0 if expired)
 */
export const getTrialDaysRemaining = (trialEndsAt: string | null): number => {
  if (!trialEndsAt) return 0;

  const now = new Date();
  const end = new Date(trialEndsAt);
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Format trial countdown for display
 * @param trialEndsAt - Trial end date string
 * @returns Human-readable countdown string
 */
export const formatTrialCountdown = (trialEndsAt: string | null): string => {
  const days = getTrialDaysRemaining(trialEndsAt);

  if (days === 0) return 'Trial expired';
  if (days === 1) return '1 day remaining';
  return `${days} days remaining`;
};

/**
 * Check if trial is expiring soon (within 3 days)
 * @param trialEndsAt - Trial end date string
 * @returns True if expiring soon
 */
export const isTrialExpiringSoon = (trialEndsAt: string | null): boolean => {
  const days = getTrialDaysRemaining(trialEndsAt);
  return days > 0 && days <= 3;
};

/**
 * Sort modules by name
 * @param modules - List of modules
 * @returns Sorted modules
 */
export const sortModulesByName = (modules: Module[]): Module[] => {
  return [...modules].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Filter modules by search query
 * @param modules - List of modules
 * @param query - Search query
 * @returns Filtered modules
 */
export const filterModules = (modules: Module[], query: string): Module[] => {
  const lowerQuery = query.toLowerCase();
  return modules.filter(
    m =>
      m.name.toLowerCase().includes(lowerQuery) ||
      m.key.toLowerCase().includes(lowerQuery) ||
      m.description.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Check if module is enabled for organization
 * @param moduleKey - Module key
 * @param orgModules - Organization's modules
 * @returns True if enabled and not disabled
 */
export const isModuleEnabled = (
  moduleKey: string,
  orgModules: OrganizationModule[]
): boolean => {
  const orgModule = orgModules.find(om => om.module?.key === moduleKey);
  if (!orgModule) return false;

  // Check if disabled
  if (orgModule.disabledAt) return false;

  // Check if trial expired
  if (orgModule.isTrial && orgModule.trialEndsAt) {
    const trialEnd = new Date(orgModule.trialEndsAt);
    if (trialEnd < new Date()) return false;
  }

  return true;
};
